// DEPLOY-PROBE-bump

/**
 * ConstructionEmpPreview Component
 * 
 * Main component for displaying and managing construction worker payslip previews.
 * Handles both admin view (full access) and employee view (limited access).
 * 
 * Features:
 * - Payslip data fetching and display
 * - Settings management (colors, fonts, field visibility)
 * - PDF generation
 * - Employee-specific data filtering
 * - Site, year, and month selection
 * - Responsive design support
 * 
 * @component
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../css/OfficeEmpPreview.css';
import '../../css/ConstructionSettings.css';
import { ConstructionTableComponent } from './ConstructionTableComponent';
import { ConstructionBonusTableComponent } from './ConstructionBonusTableComponent';
import { SettingsModal } from './SettingsModal';
import { ControlsSection } from './ControlsSection';
import {
  loadSettings,
  saveSettings,
  ConstructionPayslipSettings,
  getDefaultSettings,
  getDefaultBonusSettings,
  ConstructionSettingsDataType,
} from './SettingsUtils';
import { handlePdfDownload } from './PdfDownloadUtils';
import { fileUploadService } from '../../../../services/FileUploadService';
import ConstructionWorkerService from '../../../../services/ConstructionWorkerService';
import {
  CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE,
  filterConstructionBonusMonths,
} from '../../../../utils/constructionBonusContract';
import {
  constructionFurikomiItems,
  constructionPaymentItems,
  constructionDeductionItems,
  constructionSonotaItems,
  constructionAttendanceItems,
  constructionNotesItems,
  constructionBonusFurikomiItems,
  constructionBonusPaymentItems,
  constructionBonusDeductionItems,
  constructionBonusNotesItems,
  constructionBonusSonotaItems,
} from './DataConfiguration';

/**
 * Props interface for ConstructionEmpPreview component.
 */
interface ConstructionEmpPreviewProps {
  isEmployeeView?: boolean;        // Whether this is employee view (limited access) or admin view
  currentEmployeeCode?: string;    // Employee code for employee view (automatically loads data)
  /**
   * When 本社 empCode does not match construction_workers.emp_code, lookup by code fails.
   * Use login/session construction name or 本社氏名 so self-service 現場 matches admin search-by-name.
   */
  fallbackConstructionWorkerName?: string;
  /** 担当現場管理者: lock preview to this genba (must match login {@code assignedGenbaName}). */
  forcedAssignedGenba?: string;
  /** Hide 賞与データ toggle (給与明細プレビュー for 担当現場管理者). */
  hideBonusTab?: boolean;
}

/**
 * ConstructionEmpPreview Component
 * 
 * Main preview component for construction worker payslips.
 * Supports both admin view (full functionality) and employee view (limited access).
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const ConstructionEmpPreview: React.FC<ConstructionEmpPreviewProps> = ({ 
  isEmployeeView = false, 
  currentEmployeeCode,
  fallbackConstructionWorkerName,
  forcedAssignedGenba,
  hideBonusTab = false,
}) => {
  /**
   * State management:
   * - dataType: Type of payslip data ('construction')
   * - selectedYear/Month: Selected year and month for payslip
   * - empName: Selected employee name (main state)
   * - tempEmpName: Temporary employee name for input field (allows typing without immediate search)
   * - selectedSite: Selected construction site
   * - showSettings: Whether settings modal is open
   * - payslipSettings: Current payslip appearance settings
   * - showTable: Whether to show payslip table (always true currently)
   * - constructionData: Fetched payslip data
   * - availableYears/Months/Sites: Available options for dropdowns
   * - warningMessage: Warning message to display to user
   * - hasSearched: Whether a search has been performed (for conditional rendering)
   */
  const [payslipKind, setPayslipKind] = useState<'salary' | 'bonus'>('salary');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [empName, setEmpName] = useState<string>('');              // Main employee name state
  const [tempEmpName, setTempEmpName] = useState<string>('');     // Temporary state for input field
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [payslipSettings, setPayslipSettings] = useState<ConstructionPayslipSettings | null>(null);
  const [showTable, setShowTable] = useState<boolean>(true);
  const [constructionData, setConstructionData] = useState<any>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<number[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [availableSites, setAvailableSites] = useState<string[]>([]);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const settingsDataType: ConstructionSettingsDataType =
    payslipKind === 'bonus' ? CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE : 'construction';

  const maxNumericChoice = (values: number[]) =>
    values.length > 0 ? Math.max(...values) : undefined;

  const maxPeriodChoice = (periods: string[]) => {
    const allowed = filterConstructionBonusMonths(periods);
    if (allowed.length === 0) return undefined;
    const nums = allowed.map((p) => parseInt(p, 10)).filter((n) => !Number.isNaN(n));
    if (nums.length === 0) return undefined;
    return String(Math.max(...nums));
  };

  /**
   * Admin preview: default year/month to previous calendar month on first open.
   * Employee / 自分の給与明細: latest available period is set after worker data loads.
   */
  useEffect(() => {
    if (isEmployeeView) return;
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const y = String(prev.getFullYear());
    const m = String(prev.getMonth() + 1);
    setSelectedYear((prevY) => (prevY ? prevY : y));
    setSelectedMonth((prevM) => (prevM ? prevM : m));
  }, [isEmployeeView]);

  useEffect(() => {
    if (hideBonusTab && payslipKind === 'bonus') {
      setPayslipKind('salary');
    }
  }, [hideBonusTab, payslipKind]);
  
  /**
   * Ref for table element (used for PDF generation).
   */
  const tableRef = useRef<HTMLDivElement>(null);
  const forcedGenbaApplyKey = useRef<string | null>(null);

  /** 担当現場管理者: site fixed; global month/year APIs are forbidden — use genba-scoped endpoints. */
  const lockedAssignedGenba =
    forcedAssignedGenba?.trim() && !isEmployeeView ? forcedAssignedGenba.trim() : '';

  /**
   * Loads site/year/month lists for dropdowns.
   * Admin: all sites (not narrowed by selection). Employee view: only sites with payslip data for that worker.
   */
  /**
   * Load year/month/site dropdown options.
   * Salary admin:
   * - No name: 現場 → 年 → 月 (all sites; years/months for selected site)
   * - Name entered: 現場 list = sites that person worked; years/months for person (+ site/year)
   */
  const loadFullFilterOptions = useCallback(
    async (
      yearForTimeFilter?: string,
      siteOverride?: string,
      nameOverride?: string
    ): Promise<{ years: number[]; months: number[]; periods: string[] }> => {
      try {
        const workerName =
          nameOverride !== undefined
            ? nameOverride.trim()
            : (
                empName ||
                (isEmployeeView
                  ? tempEmpName || fallbackConstructionWorkerName || ''
                  : '') ||
                ''
              ).trim();
        const siteForFilters = (siteOverride ?? selectedSite)?.trim() || '';
        const adminGenbaScope =
          lockedAssignedGenba || (!isEmployeeView && siteForFilters ? siteForFilters : '');

        if (payslipKind === 'bonus') {
          if (lockedAssignedGenba) {
            setAvailableSites([lockedAssignedGenba]);
          } else if (isEmployeeView) {
            if (workerName) {
              const sites =
                await fileUploadService.getConstructionBonusAvailableGenbasByEmployee(workerName);
              setAvailableSites(sites);
            } else {
              setAvailableSites([]);
            }
          } else {
            const sites = await fileUploadService.getConstructionBonusAvailableGenbas();
            setAvailableSites(sites);
          }

          let years: number[];
          if (workerName) {
            // Bonus: no genba UI — years by employee (admin search or employee view)
            years = await fileUploadService.getConstructionBonusAvailableYearsByEmployee(workerName);
          } else if (adminGenbaScope) {
            years = await fileUploadService.getConstructionBonusAvailableYearsByGenba(adminGenbaScope);
          } else {
            years = await fileUploadService.getConstructionBonusAvailableYears();
          }
          setAvailableYears(years);
          setAvailableMonths([]);

          let periods: string[] = [];
          if (yearForTimeFilter?.trim()) {
            const yearNum = parseInt(yearForTimeFilter, 10);
            if (!Number.isNaN(yearNum)) {
              if (workerName) {
                periods =
                  await fileUploadService.getConstructionBonusAvailablePeriodsByEmployeeAndYear(
                    workerName,
                    yearNum
                  );
              } else if (adminGenbaScope) {
                periods = await fileUploadService.getConstructionBonusAvailablePeriodsByGenbaAndYear(
                  adminGenbaScope,
                  yearNum
                );
              } else {
                periods = await fileUploadService.getConstructionBonusAvailablePeriodsByYear(yearNum);
              }
              periods = filterConstructionBonusMonths(periods);
            }
          }
          setAvailablePeriods(periods);
          return { years, months: [], periods };
        }

        if (lockedAssignedGenba) {
          setAvailableSites([lockedAssignedGenba]);
        } else if (workerName) {
          const sites =
            await fileUploadService.getConstructionAvailableGenbasByEmployee(workerName);
          setAvailableSites(sites);
        } else if (isEmployeeView) {
          setAvailableSites([]);
        } else {
          const sites = await fileUploadService.getConstructionAvailableGenbas();
          setAvailableSites(sites);
        }

        let years: number[];
        if (workerName) {
          years = siteForFilters
            ? await fileUploadService.getConstructionAvailableYearsByEmployeeAndGenba(
                workerName,
                siteForFilters
              )
            : await fileUploadService.getConstructionAvailableYearsByEmployee(workerName);
        } else if (adminGenbaScope) {
          years = await fileUploadService.getConstructionAvailableYearsByGenba(adminGenbaScope);
        } else {
          years = await fileUploadService.getConstructionAvailableYears();
        }
        setAvailableYears(years);
        setAvailablePeriods([]);

        let months: number[] = [];
        if (yearForTimeFilter?.trim()) {
          const yearNum = parseInt(yearForTimeFilter, 10);
          if (!Number.isNaN(yearNum)) {
            if (workerName) {
              months = siteForFilters
                ? await fileUploadService.getConstructionAvailableMonthsByEmployeeAndGenbaAndYear(
                    workerName,
                    siteForFilters,
                    yearNum
                  )
                : await fileUploadService.getConstructionAvailableMonthsByEmployeeAndYear(
                    workerName,
                    yearNum
                  );
            } else if (adminGenbaScope) {
              months = await fileUploadService.getConstructionAvailableMonthsByGenbaAndYear(
                adminGenbaScope,
                yearNum
              );
            } else {
              months = await fileUploadService.getConstructionAvailableMonthsByYear(yearNum);
            }
          }
        } else if (!isEmployeeView && !adminGenbaScope && !workerName) {
          months = await fileUploadService.getConstructionAvailableMonths();
        }
        setAvailableMonths(months);
        return { years, months, periods: [] };
      } catch (error) {
        console.error('Error loading full filter options:', error);
        return { years: [], months: [], periods: [] };
      }
    },
    [
      payslipKind,
      lockedAssignedGenba,
      isEmployeeView,
      selectedSite,
      empName,
      tempEmpName,
      fallbackConstructionWorkerName,
    ]
  );

  /**
   * Initialize employee name for employee view.
   * 
   * When in employee view, automatically fetches the worker's name from their employee code
   * and loads their available sites and years. Also automatically fetches payslip data.
   * 
   * Runs only when isEmployeeView or currentEmployeeCode changes.
   */
  useEffect(() => {
    if (isEmployeeView && currentEmployeeCode) {
      /**
       * Fetch construction worker's name from employee code.
       * If no row for 本社コード, use fallbackConstructionWorkerName (session 現場氏名 or 本社氏名)
       * so 自分の給与明細 matches admin name search.
       */
      const fetchWorkerName = async () => {
        const tryLoadByName = async (name: string) => {
          const n = name.trim();
          if (!n) return;
          setEmpName(n);
          setTempEmpName(n);
          await loadFullFilterOptions();
          await fetchEmployeeData(n);
        };

        try {
          const response = await ConstructionWorkerService.getConstructionWorkerByEmpCode(currentEmployeeCode);
          if (response.success && response.data) {
            const workerName = response.data.name;
            setEmpName(workerName);
            setTempEmpName(workerName);

            await loadFullFilterOptions();
            await fetchEmployeeData(workerName);
          } else if (fallbackConstructionWorkerName?.trim()) {
            await tryLoadByName(fallbackConstructionWorkerName);
          } else {
            setEmpName(currentEmployeeCode);
            setTempEmpName(currentEmployeeCode);
          }
        } catch (error) {
          if (fallbackConstructionWorkerName?.trim()) {
            await tryLoadByName(fallbackConstructionWorkerName);
          } else {
            setEmpName(currentEmployeeCode);
            setTempEmpName(currentEmployeeCode);
          }
        }
      };

      fetchWorkerName();
    }
  }, [isEmployeeView, currentEmployeeCode, fallbackConstructionWorkerName]);

  /**
   * Load payslip settings on component mount.
   * 
   * Fetches saved settings from API (with localStorage fallback) and applies them.
   */
  useEffect(() => {
    const loadInitialSettings = async () => {
      try {
        const settings = await loadSettings(settingsDataType);
        setPayslipSettings(settings);
      } catch (error) {
        console.error('Error loading payslip settings:', error);
      }
    };

    void loadInitialSettings();
  }, [settingsDataType]);

  /**
   * Apply font and color settings to document root CSS variables.
   * 
   * Updates CSS custom properties on document root when settings change,
   * allowing all payslip components to use the same styling.
   */
  React.useEffect(() => {
    if (payslipSettings) {
      // Apply typography settings
      document.documentElement.style.setProperty('--payslip-font-size', payslipSettings.fontSize);
      document.documentElement.style.setProperty('--payslip-font-family', payslipSettings.fontFamily);
      
      // Apply color settings for main table
      document.documentElement.style.setProperty('--payslip-border-color', payslipSettings.borderColor);
      document.documentElement.style.setProperty('--payslip-background-color', payslipSettings.backgroundColor);
      
      // Apply color settings for roumuhi (labor cost) section
      document.documentElement.style.setProperty('--payslip-roumuhi-border-color', payslipSettings.roumuhiBorderColor);
      document.documentElement.style.setProperty('--payslip-roumuhi-background-color', payslipSettings.roumuhiBackgroundColor);
    }
  }, [payslipSettings]);

  /** Load full dropdown options on mount / when filters or kind change. */
  useEffect(() => {
    void loadFullFilterOptions(selectedYear || undefined);
  }, [loadFullFilterOptions, selectedYear]);

  /**
   * Among sites this worker has data for, pick the chronologically latest year/month (+ that site).
   */
  const resolveLatestSalarySelection = useCallback(
    async (
      name: string,
      sites: string[]
    ): Promise<{ site: string; year: string; month: string } | undefined> => {
      const candidates = await Promise.all(
        sites.map(async (site) => {
          try {
            const years =
              await fileUploadService.getConstructionAvailableYearsByEmployeeAndGenba(name, site);
            if (years.length === 0) return null;
            const year = maxNumericChoice(years)!;
            const months =
              await fileUploadService.getConstructionAvailableMonthsByEmployeeAndGenbaAndYear(
                name,
                site,
                year
              );
            if (months.length === 0) return null;
            const month = maxNumericChoice(months)!;
            return { site, year, month, score: year * 100 + month };
          } catch {
            return null;
          }
        })
      );
      const best = candidates
        .filter((c): c is { site: string; year: number; month: number; score: number } => c != null)
        .sort((a, b) => b.score - a.score)[0];
      if (!best) return undefined;
      return { site: best.site, year: String(best.year), month: String(best.month) };
    },
    []
  );

  /**
   * Sites for this employee only — used for auto-select when exactly one site; does not change dropdown lists.
   */
  const fetchEmployeeSitesForAutoPick = useCallback(async (name: string): Promise<string[]> => {
    if (!name.trim()) {
      return [];
    }
    if (forcedAssignedGenba?.trim() && !isEmployeeView) {
      return [forcedAssignedGenba.trim()];
    }
    try {
      return payslipKind === 'bonus'
        ? await fileUploadService.getConstructionBonusAvailableGenbasByEmployee(name)
        : await fileUploadService.getConstructionAvailableGenbasByEmployee(name);
    } catch {
      return [];
    }
  }, [forcedAssignedGenba, isEmployeeView, payslipKind]);

  /**
   * After site change: preview payslip if current person has data at site+year+month; else clear name (admin).
   */
  const applyPayslipForSiteChange = useCallback(
    async (
      site: string,
      yearStr: string,
      timeStr: string,
      nameOverride?: string
    ) => {
      const name = (nameOverride ?? empName ?? tempEmpName).trim();
      if (!name || !yearStr || !timeStr) {
        return;
      }
      if (payslipKind === 'salary' && !site) {
        return;
      }

      const year = parseInt(yearStr, 10);
      if (Number.isNaN(year)) {
        return;
      }

      try {
        if (payslipKind === 'bonus') {
          const row = await fileUploadService.getConstructionBonusData(name, year, timeStr);
          if (row) {
            if (row.genbaName) {
              setSelectedSite(String(row.genbaName));
            }
            setEmpName(name);
            setTempEmpName(name);
            setHasSearched(true);
            setConstructionData(row);
            setWarningMessage('');
            return;
          }
          setConstructionData(null);
          if (isEmployeeView || lockedAssignedGenba) {
            setWarningMessage(
              `指定された条件（年: ${year}, 月: ${timeStr}）で賞与データが見つかりません。`
            );
          } else {
            setEmpName('');
            setTempEmpName('');
            setHasSearched(false);
            setWarningMessage('');
          }
          return;
        }

        const row = await fileUploadService.getConstructionSalaryData(
          name,
          year,
          parseInt(timeStr, 10),
          site
        );

        if (row) {
          setEmpName(name);
          setTempEmpName(name);
          setHasSearched(true);
          setConstructionData(row);
          setWarningMessage('');
          return;
        }

        setConstructionData(null);
        if (isEmployeeView || lockedAssignedGenba) {
          setWarningMessage(
            `指定された条件（年: ${year}, 月: ${timeStr}, 現場: ${site}）で給与データが見つかりません。`
          );
        } else {
          setEmpName('');
          setTempEmpName('');
          setHasSearched(false);
          setWarningMessage('');
        }
      } catch {
        setConstructionData(null);
        if (!isEmployeeView && !lockedAssignedGenba) {
          setEmpName('');
          setTempEmpName('');
          setHasSearched(false);
          setWarningMessage('');
        }
      }
    },
    [empName, tempEmpName, isEmployeeView, lockedAssignedGenba, payslipKind]
  );

  /**
   * Handles site selection. Dropdown lists stay full; only selection values update.
   */
  const handleSiteChange = useCallback(
    async (
      site: string,
      workerNameForQueries?: string,
      options?: { fromEmployeeSearch?: boolean }
    ): Promise<{ site: string; year: string; month: string; period: string } | undefined> => {
      const prevYear = selectedYear;
      const prevTime = payslipKind === 'bonus' ? selectedPeriod : selectedMonth;
      setSelectedSite(site);

      if (!site) {
        setSelectedYear('');
        setSelectedMonth('');
        setSelectedPeriod('');
        setConstructionData(null);
        setHasSearched(false);
        setWarningMessage('');
        await loadFullFilterOptions();
        return undefined;
      }

      if (!options?.fromEmployeeSearch) {
        setConstructionData(null);
        setHasSearched(false);
        setWarningMessage('');
      }

      try {
        const { years } = await loadFullFilterOptions(prevYear || undefined, site);

        if (years.length === 0) {
          setSelectedYear('');
          setSelectedMonth('');
          setSelectedPeriod('');
          return undefined;
        }

        const useLatestForSiteScope = isEmployeeView || Boolean(site?.trim());

        const yearPick = useLatestForSiteScope
          ? maxNumericChoice(years)!
          : (() => {
              const prevY = parseInt(prevYear.trim(), 10);
              return !Number.isNaN(prevY) && years.includes(prevY) ? prevY : maxNumericChoice(years)!;
            })();
        setSelectedYear(String(yearPick));

        const timeResult = await loadFullFilterOptions(String(yearPick), site);
        const timeValues = payslipKind === 'bonus' ? timeResult.periods : timeResult.months;

        if (timeValues.length === 0) {
          if (payslipKind === 'bonus') {
            setSelectedPeriod('');
          } else {
            setSelectedMonth('');
          }
          return { site, year: String(yearPick), month: '', period: '' };
        }

        let timePick: string;
        if (payslipKind === 'bonus') {
          const periods = timeResult.periods;
          timePick = useLatestForSiteScope
            ? maxPeriodChoice(periods)!
            : (() => {
                const prevP = prevTime.trim();
                return periods.includes(prevP) ? prevP : maxPeriodChoice(periods)!;
              })();
          setSelectedPeriod(timePick);
        } else {
          const months = timeResult.months;
          const monthPick = useLatestForSiteScope
            ? maxNumericChoice(months)!
            : (() => {
                const prevM = parseInt(prevTime.trim(), 10);
                return !Number.isNaN(prevM) && months.includes(prevM) ? prevM : maxNumericChoice(months)!;
              })();
          setSelectedMonth(String(monthPick));
          timePick = String(monthPick);
        }

        const result = {
          site,
          year: String(yearPick),
          month: payslipKind === 'salary' ? timePick : '',
          period: payslipKind === 'bonus' ? timePick : '',
        };

        if (!options?.fromEmployeeSearch) {
          await applyPayslipForSiteChange(
            site,
            result.year,
            timePick,
            workerNameForQueries?.trim() || undefined
          );
        }

        return result;
      } catch (error) {
        console.error('Error updating site selection:', error);
        return undefined;
      }
    },
    [
      selectedYear,
      selectedMonth,
      selectedPeriod,
      payslipKind,
      isEmployeeView,
      loadFullFilterOptions,
      applyPayslipForSiteChange,
    ]
  );

  useEffect(() => {
    const g = forcedAssignedGenba?.trim();
    if (isEmployeeView || !g) {
      forcedGenbaApplyKey.current = null;
      return;
    }
    if (forcedGenbaApplyKey.current === g) {
      return;
    }
    forcedGenbaApplyKey.current = g;
    setAvailableSites([g]);
    void handleSiteChange(g);
  }, [isEmployeeView, forcedAssignedGenba, handleSiteChange]);

  const fetchPayslipForSelection = useCallback(
    async (name: string, yearStr: string, timeStr: string, siteStr?: string) => {
      const year = parseInt(yearStr, 10);
      if (Number.isNaN(year)) {
        return;
      }
      try {
        if (payslipKind === 'bonus') {
          const row = await fileUploadService.getConstructionBonusData(name, year, timeStr);
          if (row) {
            if (row.genbaName) {
              setSelectedSite(String(row.genbaName));
            }
            setConstructionData(row);
            setWarningMessage('');
            setHasSearched(true);
          } else {
            setConstructionData(null);
            setWarningMessage(
              `指定された条件（年: ${year}, 月: ${timeStr}）で賞与データが見つかりません。`
            );
          }
          return;
        }

        if (!siteStr?.trim()) {
          setConstructionData(null);
          setWarningMessage('年、月、現場を選択してください。');
          return;
        }

        const row = await fileUploadService.getConstructionSalaryData(
          name,
          year,
          parseInt(timeStr, 10),
          siteStr
        );

        if (row) {
          setConstructionData(row);
          setWarningMessage('');
          setHasSearched(true);
        } else {
          setConstructionData(null);
          setWarningMessage(
            `指定された条件（年: ${year}, 月: ${timeStr}, 現場: ${siteStr}）で給与データが見つかりません。`
          );
        }
      } catch {
        setWarningMessage(
          payslipKind === 'bonus'
            ? '建設従業員賞与データの取得中にエラーが発生しました。'
            : '建設従業員給与データの取得中にエラーが発生しました。'
        );
        setConstructionData(null);
      }
    },
    [payslipKind]
  );

  /**
   * Fetches employee data and payslip information by employee name.
   *
   * Process:
   * 1. Validates employee name
   * 2. Searches for employee in database
   * 3. Salary: loads sites that person worked; auto-previews latest slip across those sites
   * 4. Bonus: loads latest year/period without site selection
   *
   * @param name - Employee name to search for
   * @param options.skipSiteRebind - When true (year/month change with locked site), only reload payslip
   */
  const fetchEmployeeData = useCallback(
    async (name: string, options?: { skipSiteRebind?: boolean }) => {
      // Clear state if name is empty
      if (!name.trim()) {
        setWarningMessage('');
        setHasSearched(false);
        setConstructionData(null);
        return;
      }

      const timeForFetch = payslipKind === 'bonus' ? selectedPeriod : selectedMonth;

      // Bonus: no genba selection — reload by name + year + period only
      if (payslipKind === 'bonus') {
        if (options?.skipSiteRebind && selectedYear && timeForFetch) {
          await fetchPayslipForSelection(name, selectedYear, timeForFetch);
          return;
        }

        try {
          const employee = await fileUploadService.searchConstructionBonusEmployeeByName(name);
          if (!employee) {
            setConstructionData(null);
            setWarningMessage(
              `従業員名 ${name} の建設従業員賞与が見つかりません。正しい名前を入力してください。`
            );
            setHasSearched(true);
            return;
          }

          setWarningMessage('');
          setHasSearched(true);
          setEmpName(name);

          const { years } = await loadFullFilterOptions(selectedYear || undefined);
          if (years.length === 0) {
            setSelectedYear('');
            setSelectedPeriod('');
            setSelectedSite('');
            setConstructionData(null);
            setWarningMessage('賞与データが見つかりません。');
            return;
          }

          const prevY = parseInt(selectedYear.trim(), 10);
          const yearPick =
            isEmployeeView || !selectedYear
              ? maxNumericChoice(years)!
              : !Number.isNaN(prevY) && years.includes(prevY)
                ? prevY
                : maxNumericChoice(years)!;
          setSelectedYear(String(yearPick));

          const { periods } = await loadFullFilterOptions(String(yearPick));
          if (periods.length === 0) {
            setSelectedPeriod('');
            setConstructionData(null);
            setWarningMessage('年、月を選択してください。');
            return;
          }

          const prevP = selectedPeriod.trim();
          const periodPick =
            isEmployeeView || !prevP
              ? maxPeriodChoice(periods)!
              : periods.includes(prevP)
                ? prevP
                : maxPeriodChoice(periods)!;
          setSelectedPeriod(periodPick);

          await fetchPayslipForSelection(name, String(yearPick), periodPick);
        } catch {
          setWarningMessage('建設従業員情報の取得中にエラーが発生しました。');
          setHasSearched(true);
        }
        return;
      }

      const siteForFetch = selectedSite || lockedAssignedGenba || '';
      if (options?.skipSiteRebind && siteForFetch && selectedYear && timeForFetch) {
        await fetchPayslipForSelection(name, selectedYear, timeForFetch, siteForFetch);
        return;
      }

      try {
        const employee = await fileUploadService.searchConstructionEmployeeByName(name);

        if (employee) {
          setWarningMessage('');
          setHasSearched(true);
          setEmpName(name);

          const sites = await fetchEmployeeSitesForAutoPick(name);
          await loadFullFilterOptions(selectedYear || undefined, undefined, name);

          if (sites.length === 0) {
            setConstructionData(null);
            setSelectedSite('');
            setSelectedYear('');
            setSelectedMonth('');
            setWarningMessage('年、月、現場を選択してください。');
            return;
          }

          const latest = await resolveLatestSalarySelection(name, sites);
          if (latest) {
            setSelectedSite(latest.site);
            setSelectedYear(latest.year);
            setSelectedMonth(latest.month);
            await loadFullFilterOptions(latest.year, latest.site, name);
            await fetchPayslipForSelection(name, latest.year, latest.month, latest.site);
            return;
          }

          setConstructionData(null);
          setWarningMessage('年、月、現場を選択してください。');
        } else {
          setConstructionData(null);
          setWarningMessage(
            `従業員名 ${name} の建設従業員が見つかりません。正しい名前を入力してください。`
          );
          setHasSearched(true);
        }
      } catch (error) {
        setWarningMessage('建設従業員情報の取得中にエラーが発生しました。');
        setHasSearched(true);
      }
    },
    [
      selectedYear,
      selectedMonth,
      selectedPeriod,
      selectedSite,
      lockedAssignedGenba,
      payslipKind,
      isEmployeeView,
      fetchEmployeeSitesForAutoPick,
      resolveLatestSalarySelection,
      loadFullFilterOptions,
      fetchPayslipForSelection,
    ]
  );

  /**
   * Handles year selection — month list is global, or genba-scoped when a site is selected.
   */
  const handleYearChange = useCallback(
    async (year: string) => {
      setSelectedYear(year);
      setSelectedMonth('');
      setSelectedPeriod('');
      if (year) {
        await loadFullFilterOptions(year);
      } else {
        await loadFullFilterOptions();
      }
    },
    [loadFullFilterOptions]
  );

  const handlePayslipKindChange = useCallback(
    async (kind: 'salary' | 'bonus') => {
      if (hideBonusTab && kind === 'bonus') return;
      if (kind === payslipKind) return;
      setPayslipKind(kind);
      setConstructionData(null);
      setHasSearched(false);
      setWarningMessage('');
      setSelectedMonth('');
      setSelectedPeriod('');
      setSelectedYear('');
      if (!isEmployeeView) {
        setEmpName('');
        setTempEmpName('');
      }
      if (kind === 'bonus') {
        setSelectedSite('');
      } else if (forcedAssignedGenba?.trim() && !isEmployeeView) {
        setSelectedSite(forcedAssignedGenba.trim());
      } else if (!isEmployeeView) {
        setSelectedSite('');
      }
      const settings =
        kind === 'bonus'
          ? await loadSettings(CONSTRUCTION_BONUS_SETTINGS_DATA_TYPE)
          : await loadSettings('construction');
      setPayslipSettings(settings);
      // Admin: restore no-name filter lists after clearing name
      if (!isEmployeeView) {
        void loadFullFilterOptions(undefined, undefined, '');
      }
    },
    [payslipKind, forcedAssignedGenba, isEmployeeView, loadFullFilterOptions, hideBonusTab]
  );

  /**
   * Employee view: when toggling 給与/賞与, re-bind latest site/year/month|period
   * the same way login auto-loads the latest salary slip.
   * Initial salary load is handled by fetchWorkerName — skip until kind actually changes.
   */
  const prevPayslipKindRef = useRef(payslipKind);
  useEffect(() => {
    if (!isEmployeeView) return;
    const name = (empName || tempEmpName).trim();
    if (!name) return;
    if (prevPayslipKindRef.current === payslipKind) return;
    prevPayslipKindRef.current = payslipKind;
    void fetchEmployeeData(name);
  }, [payslipKind, isEmployeeView, empName, tempEmpName, fetchEmployeeData]);

  useEffect(() => {
    if (!empName || !selectedYear) {
      return;
    }
    const timeReady = payslipKind === 'bonus' ? selectedPeriod : selectedMonth;
    if (!timeReady) {
      return;
    }
    if (payslipKind === 'salary') {
      const site = selectedSite || lockedAssignedGenba;
      if (!site) {
        return;
      }
    }
    void fetchEmployeeData(empName, { skipSiteRebind: true });
  }, [
    selectedYear,
    selectedMonth,
    selectedPeriod,
    payslipKind,
    empName,
    selectedSite,
    lockedAssignedGenba,
    fetchEmployeeData,
  ]);

  /**
   * Updates payslip settings and saves them to API.
   * 
   * Updates local state and persists settings to backend (with localStorage fallback).
   * 
   * @param newSettings - New payslip settings to apply
   */
  const updateSettings = async (newSettings: ConstructionPayslipSettings) => {
    setPayslipSettings(newSettings);
    try {
      await saveSettings(newSettings, settingsDataType);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleClear = () => {
    if (!isEmployeeView) {
      setEmpName('');
      setTempEmpName('');
    }
    if (forcedAssignedGenba?.trim() && !isEmployeeView) {
      setSelectedSite(forcedAssignedGenba.trim());
    } else {
      setSelectedSite('');
    }
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedPeriod('');
    setConstructionData(null);
    setWarningMessage('');
    setHasSearched(false);
    // Force no-name filter mode (state empName may still be stale in this closure)
    void loadFullFilterOptions(undefined, undefined, isEmployeeView ? undefined : '');
  };

  const handlePdfDownloadClick = () => {
    const defaultSettings =
      payslipKind === 'bonus' ? getDefaultBonusSettings() : getDefaultSettings();
    const siteForPdf =
      payslipKind === 'bonus'
        ? String(constructionData?.genbaName || selectedSite || '').trim()
        : selectedSite;
    handlePdfDownload(
      tableRef,
      selectedYear,
      selectedMonth,
      empName,
      siteForPdf,
      payslipSettings || defaultSettings,
      payslipKind,
      selectedPeriod
    );
  };

  const activeSettings =
    payslipSettings ??
    (payslipKind === 'bonus' ? getDefaultBonusSettings() : getDefaultSettings());

  return (
    <div className="office-emp-preview">
      {!isEmployeeView && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          settings={activeSettings}
          onSettingsChange={updateSettings}
          bonusMode={payslipKind === 'bonus'}
          furikomiItems={
            payslipKind === 'bonus' ? constructionBonusFurikomiItems : constructionFurikomiItems
          }
          paymentItems={
            payslipKind === 'bonus' ? constructionBonusPaymentItems : constructionPaymentItems
          }
          deductionItems={
            payslipKind === 'bonus' ? constructionBonusDeductionItems : constructionDeductionItems
          }
          attendanceItems={constructionAttendanceItems}
          notesItems={
            payslipKind === 'bonus' ? constructionBonusNotesItems : constructionNotesItems
          }
          sonotaItems={
            payslipKind === 'bonus' ? constructionBonusSonotaItems : constructionSonotaItems
          }
        />
      )}

      <ControlsSection
        payslipKind={payslipKind}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        selectedPeriod={selectedPeriod}
        empName={tempEmpName}
        selectedSite={selectedSite}
        availableYears={availableYears}
        availableMonths={availableMonths}
        availablePeriods={availablePeriods}
        availableSites={availableSites}
        onPayslipKindChange={handlePayslipKindChange}
        onYearChange={handleYearChange}
        onMonthChange={setSelectedMonth}
        onPeriodChange={setSelectedPeriod}
        onEmpNameChange={setTempEmpName}
        onEmpNameSelect={async (name) => {
          setTempEmpName(name);
          await fetchEmployeeData(name);
        }}
        onSiteChange={handleSiteChange}
        onClear={handleClear}
        onPdfDownload={handlePdfDownloadClick}
        onSettingsClick={() => setShowSettings(!showSettings)}
        isEmployeeView={isEmployeeView}
        siteSelectLocked={Boolean(forcedAssignedGenba?.trim()) && !isEmployeeView}
        hideBonusTab={hideBonusTab}
      />

      {/* Warning Message */}
      {warningMessage && (
        <div className="warning-message" style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          padding: '12px',
          margin: '10px 0',
          color: '#856404',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⚠️ {warningMessage}
        </div>
      )}

      {/* Construction Worker Payslip Table */}
      <div className="payslip-scale-wrapper">
        <div className="payslip-scale-inner">
          <div className="table-area">
            <div ref={tableRef}>
              {payslipKind === 'bonus' ? (
                <ConstructionBonusTableComponent
                  key={`bonus-${selectedSite}-${selectedYear}-${selectedPeriod}-${empName || 'none'}-${constructionData ? 'loaded' : 'empty'}`}
                  selectedYear={selectedYear}
                  selectedPeriod={selectedPeriod}
                  empName={empName}
                  selectedSite={selectedSite}
                  hasSearched={hasSearched}
                  constructionData={constructionData}
                  payslipSettings={activeSettings}
                  furikomiItems={constructionBonusFurikomiItems}
                  paymentItems={constructionBonusPaymentItems}
                  deductionItems={constructionBonusDeductionItems}
                  notesItems={constructionBonusNotesItems}
                  sonotaItems={constructionBonusSonotaItems}
                />
              ) : (
                <ConstructionTableComponent
                  key={`salary-${selectedSite}-${selectedYear}-${selectedMonth}-${empName || 'none'}-${constructionData ? 'loaded' : 'empty'}`}
                  dataType="construction"
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  empName={empName}
                  selectedSite={selectedSite}
                  hasSearched={hasSearched}
                  constructionData={constructionData}
                  payslipSettings={activeSettings}
                  furikomiItems={constructionFurikomiItems}
                  paymentItems={constructionPaymentItems}
                  deductionItems={constructionDeductionItems}
                  attendanceItems={constructionAttendanceItems}
                  notesItems={constructionNotesItems}
                  sonotaItems={constructionSonotaItems}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 